import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Event = {
  id: string;
  event_on: string;
  from_status?: string | null;
  to_status: string;
  note?: string | null;
};

type Workflow = {
  id: string;
  workflow_no: string;
  id_kind: string;
  status: string;
  record_version: number;
  consent_on?: string | null;
  consent_by_name?: string | null;
  consent_method?: string | null;
  consent_notes?: string | null;
  external_value?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  submitted_on?: string | null;
  issued_on?: string | null;
  verified_on?: string | null;
  status_notes?: string | null;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  pen_id?: string | null;
  apaar_id?: string | null;
  events: Event[];
};

export default function IdsWorkflowDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.ids.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-id-workflow', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Workflow>(`/education/ids/workflows/${id}`)).data,
  });
  const row = query.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-id-workflow', id] });
    queryClient.invalidateQueries({ queryKey: ['education-id-workflows'] });
  };

  const requestConsent = useMutation({
    mutationFn: () => api.post(`/education/ids/workflows/${id}/request-consent`),
    onSuccess: () => {
      message.success(t('idConsentRequested'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const recordConsent = useMutation({
    mutationFn: (values: {
      consent_on: dayjs.Dayjs;
      consent_by_name: string;
      consent_method: string;
      consent_notes?: string;
    }) =>
      api.post(`/education/ids/workflows/${id}/consent`, {
        consent_on: values.consent_on.format('YYYY-MM-DD'),
        consent_by_name: values.consent_by_name,
        consent_method: values.consent_method,
        consent_notes: values.consent_notes,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('idConsentRecorded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const transition = useMutation({
    mutationFn: (values: { status: string; external_value?: string; status_notes?: string }) =>
      api.post(`/education/ids/workflows/${id}/status`, {
        ...values,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('idStatusUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const nextOptions = (() => {
    const map: Record<string, string[]> = {
      draft: ['consent_pending', 'cancelled'],
      consent_pending: ['withdrawn', 'cancelled'],
      consented: ['submitted', 'withdrawn', 'cancelled'],
      submitted: ['issued', 'withdrawn', 'cancelled'],
      issued: ['verified', 'withdrawn'],
      verified: ['withdrawn'],
    };
    return (map[row?.status || ''] || []).map((value) => ({ value, label: value }));
  })();

  return (
    <EducationPageChrome
      title={row?.workflow_no || t('idWorkflows')}
      description={t('idWorkflowsIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/ids/workflows')}>{t('idWorkflows')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('idsExternalHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('student')}>
                {row.admission_no} — {[row.first_name, row.last_name].filter(Boolean).join(' ')}
              </Descriptions.Item>
              <Descriptions.Item label={t('idKind')}>{row.id_kind.toUpperCase()}</Descriptions.Item>
              <Descriptions.Item label={t('externalValue')}>{row.external_value || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('studentCurrentIds')}>
                PEN: {row.pen_id || '—'} / APAAR: {row.apaar_id || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('consentOn')}>{row.consent_on || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('consentBy')}>{row.consent_by_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('consentMethod')}>{row.consent_method || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('effectiveFrom')}>{row.effective_from || '—'}</Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>

        {canManage && row?.status === 'draft' ? (
          <Card size="small" title={t('requestConsent')}>
            <Button type="primary" loading={requestConsent.isPending} onClick={() => requestConsent.mutate()}>
              {t('requestConsent')}
            </Button>
          </Card>
        ) : null}

        {canManage && (row?.status === 'draft' || row?.status === 'consent_pending') ? (
          <Card size="small" title={t('recordConsent')}>
            <Form
              layout="vertical"
              style={{ maxWidth: 520 }}
              initialValues={{ consent_on: dayjs(), consent_method: 'guardian_signed' }}
              onFinish={(values) => recordConsent.mutate(values)}
            >
              <Form.Item name="consent_on" label={t('consentOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="consent_by_name" label={t('consentBy')} rules={[{ required: true }]}>
                <Input maxLength={200} />
              </Form.Item>
              <Form.Item name="consent_method" label={t('consentMethod')} rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'guardian_signed', label: t('consentGuardianSigned') },
                    { value: 'school_attested', label: t('consentSchoolAttested') },
                    { value: 'digital_ack', label: t('consentDigitalAck') },
                    { value: 'unknown', label: t('consentUnknown') },
                  ]}
                />
              </Form.Item>
              <Form.Item name="consent_notes" label={t('notes')}>
                <Input.TextArea rows={2} maxLength={1000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={recordConsent.isPending}>
                {t('recordConsent')}
              </Button>
            </Form>
          </Card>
        ) : null}

        {canManage && nextOptions.length ? (
          <Card size="small" title={t('advanceStatus')}>
            <Form layout="vertical" style={{ maxWidth: 520 }} onFinish={(values) => transition.mutate(values)}>
              <Form.Item name="status" label={t('status')} rules={[{ required: true }]}>
                <Select options={nextOptions} />
              </Form.Item>
              <Form.Item name="external_value" label={t('externalValue')} extra={t('externalValueHint')}>
                <Input maxLength={80} />
              </Form.Item>
              <Form.Item name="status_notes" label={t('notes')}>
                <Input.TextArea rows={2} maxLength={1000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={transition.isPending}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : null}

        <Card size="small" title={t('statusHistory')}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={row?.events ?? []}
            columns={[
              { title: t('date'), dataIndex: 'event_on', width: 180 },
              {
                title: t('status'),
                render: (_: unknown, e: Event) => `${e.from_status || '—'} → ${e.to_status}`,
              },
              { title: t('notes'), dataIndex: 'note', render: (v: string) => v || '—' },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
